/**
 * Analytics for the server-rendered SEO pages (site/render.js): session_start / session_end,
 * discord_click, lead_tap and client_error. Consent (stored opt-out or Global Privacy Control) is
 * checked before anything is built, and enforced again inside track(), same as the SPA.
 *
 * session_start: these pages are the Meta landings (`/games/brain-teasers?utm_source=meta…`).
 * Until Oct 27 they sent no session_start, so a paid click that read the page and left, or
 * closed the Facebook/Instagram in-app browser, was invisible: brain-1027 showed 25 Meta
 * landing views against 2 sessions, and the only trace of the rest was the in-app browser's
 * own injected-script error. A landing now starts a session with its UTMs and `landing_page`
 * (`/games/…`), so landings can be counted against Meta's numbers. Tapping Play loads
 * `/play/<slug>`, a new page with its own session_start (same anon_id, same stored UTMs):
 * count game starts per landing by anon_id, not by session_id.
 *
 * The visit's attribution is captured on load first. A paid click lands on
 * `/games/<slug>?utm_source=meta…` and the player taps Play, which leaves this page for
 * `/play/<slug>` with no utm_* in the URL: unless the landing is stored here, the SPA's
 * tracker has nothing to attribute the session (and the whole load funnel) to.
 * captureAttribution writes nothing without analytics consent.
 *
 * client_error is installed here for the same reason it is in the SPA
 * (ops/meta-load-drop-1021 could not measure errors at all).
 *
 * Every step is guarded: analytics must never be the reason a landing does not work.
 */
import { analyticsConsent, captureAttribution, createTracker, installDiscordTracking, installLeadTracking, markPaidAcquisition } from "./lib/analytics.js";
import { installErrorTracking } from "./lib/errors.js";
import { releaseGa, syncGaInternalVerdict } from "./lib/ga4.js";

const guard = (fn) => {
  try {
    return fn();
  } catch {
    return null;
  }
};

guard(() => markPaidAcquisition());
// GA4 consent updates from the consent message / analytics opt-out (no-op when GA is off).

guard(() => captureAttribution());
window.addEventListener("barumba-consent-change", () => guard(() => captureAttribution()));
// GA4 traffic_type=internal for internal accounts / IPs landing here first: these pages have
// no session sync, so ask the server once per browser (never delays a hit; no-op when GA is off).
guard(() => syncGaInternalVerdict(window));
if (!window.__barumbaGa && window.__barumbaConsentPending) {
  window.__barumbaGaAwaitVerdict = true;
  window.addEventListener("barumba-ga-ready", () => {
    const pending = guard(() => syncGaInternalVerdict(window));
    Promise.resolve(pending).finally(() => releaseGa(window));
  }, { once: true });
  window.addEventListener("barumba-ga-verdict-timeout", () => releaseGa(window));
  window.addEventListener("pagehide", () => releaseGa(window));
}

let tracker = null;
// Adopt the head bootstrap's tracker (src/error-bootstrap.js) so an early client_error and
// the session share one session_id and the hidden-at-load prefetch record.
const build = () => (tracker ||= window.__barumbaErrors?.getTracker?.() || createTracker());
guard(() => {
  let storage = null;
  try { storage = window.localStorage; } catch { /* storage blocked */ }
  if (analyticsConsent({ nav: window.navigator, storage })) build().start();
});
const lazy = window.__barumbaErrors?.tracker || {
  track: (event, props) => build().track(event, props),
  flush: (opts) => tracker?.flush(opts),
};
guard(() => installDiscordTracking({ doc: document, loc: location, tracker: lazy }));
// Category landers: the "Play today's puzzle" lead button (lead_tap; GA4 mirrors it as lead_tap).
guard(() => installLeadTracking({ doc: document, loc: location, tracker: lazy }));
if (!window.__barumbaErrors) guard(() => installErrorTracking({ win: window, doc: document, tracker: lazy }));
